import React, { useState, useRef, useEffect } from 'react';
import { QRCode } from 'react-qrcode-logo';
import { iconsPath, imagePath as imagePath1 } from '../../utils/imagePath';
import { encrypt } from '../../utils/cryptoUtils';
import ReactDOM from 'react-dom/client';
import { SimpleModal } from '../ui/NormalModal';
import store from '../../redux/store';
import { setLoading } from '../../redux/Slice/globalStates';

const PublishData = ({
  projectId,
  setPublished,
  published,
  onAfterPublish,
  category,
  redirectionCode, // Add this prop to receive the redirection code
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState('180p');
  const [imagePath, setImagePath] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [showTooltip, setShowTooltip] = useState({ index: null, show: false });
  const [downloadSize, setDownloadSize] = useState(100);
  const [codeInputs, setCodeInputs] = useState(['', '', '', '', '', '']);

  const qrRef = useRef(null);
  const modalref = useRef(null);
  const inputRefs = useRef([]);

  const isVRMode = category?.toLowerCase() === 'vr'||category?.toLowerCase() === 'mr';

  // Populate code inputs when redirectionCode is available (VR mode)
  useEffect(() => {
    if (isVRMode && redirectionCode) {
      const code = redirectionCode.toString().toUpperCase();
      const codeArray = code.split('').slice(0, 6);
      // Pad with empty strings if code is less than 6 characters
      while (codeArray.length < 6) {
        codeArray.push('');
      }
      setCodeInputs(codeArray);
    }
  }, [isVRMode, redirectionCode]);

  const handleClickOutside = (event) => {
    if (modalref.current && !modalref.current.contains(event.target)) {
      setPublished(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const options = ['180p', '360p', '480p'];
  const labels = ['Public URL', 'Embed Link', 'Spatial Grid ID'];

  // Generate URLs based on projectId (same for both modes)
  // const urls = labels.map(
  //   () =>
  //     `${process.env.REACT_APP_IMAGE_BASE_URL}/api/v1/experience/detail/${projectId}`
  // );

  const urls =
    isVRMode && redirectionCode
      ? [
          `${process.env.REACT_APP_IMAGE_BASE_URL}/api/v1/experience/redirect/${redirectionCode}`,
          `${process.env.REACT_APP_IMAGE_BASE_URL}/api/v1/experience/redirect/${redirectionCode}`,
          `${process.env.REACT_APP_IMAGE_BASE_URL}/api/v1/experience/redirect/${redirectionCode}`,
        ]
      : labels.map(
          () =>
            `${process.env.REACT_APP_IMAGE_BASE_URL}/api/v1/experience/detail/${projectId}`
        );

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileReader = new FileReader();
      fileReader.onload = (event) => {
        setLogoUrl(event.target.result);
      };
      fileReader.readAsDataURL(file);
      setImagePath(file.name);
    }
  };

  const socialIcon = [
    { id: 1, icon: imagePath1?.x },
    { id: 2, icon: imagePath1?.instagram },
    { id: 3, icon: imagePath1?.linkdin },
    { id: 4, icon: imagePath1?.youtube },
    { id: 5, icon: imagePath1?.discord },
  ];

  useEffect(() => {
    switch (selectedOption) {
      case '180p':
        setDownloadSize(100);
        break;
      case '360p':
        setDownloadSize(200);
        break;
      case '480p':
        setDownloadSize(300);
        break;
      default:
        setDownloadSize(100);
    }
  }, [selectedOption]);

  const downloadQRCode = () => {
    const tempContainer = document.createElement('div');
    document.body.appendChild(tempContainer);

    const tempQR = React.createElement(QRCode, {
      value: urls[0],
      size: downloadSize,
      logoImage: logoUrl,
      logoWidth: downloadSize * 0.2,
      logoHeight: downloadSize * 0.2,
      logoOpacity: 1,
      removeQrCodeBehindLogo: false,
      style: {
        backgroundColor: '#F5F5F5',
        padding: '0',
        borderRadius: '20px',
      },
    });

    const root = ReactDOM.createRoot(tempContainer);
    root.render(tempQR);

    setTimeout(() => {
      const qrCanvas = tempContainer.querySelector('canvas');
      if (qrCanvas) {
        const qrImage = qrCanvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = qrImage;
        link.download = `qrcode-${selectedOption}.png`;
        link.click();
      }
      root.unmount();
      tempContainer.remove();
    }, 100);
  };

  const handleCopy = (index, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setShowTooltip({ index, show: true });
      setTimeout(() => {
        setShowTooltip({ index: null, show: false });
      }, 2000);
    });
  };

  // Handle code input for VR mode
  const handleCodeInput = (index, value) => {
    if (value.length <= 1) {
      const newInputs = [...codeInputs];
      newInputs[index] = value.toUpperCase();
      setCodeInputs(newInputs);

      // Auto-focus next input
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codeInputs[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <SimpleModal
      isOpen={published}
      onClose={() => {
        setPublished('');
      }}
      className={`bg-[#131418] shadow-lg rounded-[10px]   relative ${isVRMode ? '!max-w-[430px] px-10 py-10' : '!max-w-[340px] px-6 py-6'}`}
    >
      <div
        onClick={() => {
          setPublished(false);
        }}
        className="absolute top-3 right-4"
      >
        <button className="bg-[#31333A] px-2 py-2 rounded-full">
          <img className="h-3 w-3" src={iconsPath?.group95Image} alt="" />
        </button>
      </div>

      {category === null ? (
        <div className="flex flex-col items-center justify-center p-20 gap-4">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-white"></div>
          <p className="text-white text-sm animate-pulse font-medium">Preparing Experience...</p>
        </div>
      ) : isVRMode ? (
        // VR Mode UI
        <div className="flex flex-col items-center gap-8">
          <div className="flex flex-col">
            <h1 className="text-[#F5F5F5] font-bold text-[18px] text-center">
              Enter Experience Code
            </h1>
            <p className="text-[#A6A6A6] text-[12px] text-center">
              Type the 6-character code to load your experience.
            </p>
          </div>

          {/* Code Input Boxes */}
          <div className="flex gap-2 ">
            {codeInputs.map((value, index) => (
              <React.Fragment key={index}>
                <input
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  maxLength="1"
                  value={value}
                  onChange={(e) => handleCodeInput(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled
                  className="w-12 h-12 bg-[#2B2C2F] text-[#F5F5F5] text-center text-lg rounded-[10px] border border-[#878686] outline-none"
                />

                {/* Insert dash after 3rd input */}
                {index === 2 && (
                  <span className="text-white text-xl flex items-center justify-center">
                    -
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      ) : (
        // Normal Mode UI (Original)
        <div className="flex flex-col items-center gap-4">
          <div className="flex flex-col">
            <h1 className="text-[#F5F5F5] font-bold text-[16px] text-center">
              Your Changes are Published!
            </h1>
            <p className="text-[#A6A6A6] text-[12px] text-center">
              Scan this QR code to view your experience
            </p>
          </div>

          {/* QR Code Generation */}
          <div
            ref={qrRef}
            className="border-8 border-[#CACACA] h-[130px] w-[130px] rounded-[20px] relative"
          >
            <QRCode
              value={urls[0]}
              size={300}
              logoImage={logoUrl}
              logoWidth={20}
              logoHeight={20}
              logoOpacity={1}
              removeQrCodeBehindLogo={false}
              style={{
                position: 'relative',
                top: '-3px',
                backgroundColor: '#F5F5F5',
                boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.5)',
                borderRadius: '20px',
                right: '3px',
                maxWidth: '120px',
                maxHeight: '120px',
                padding: '10px',
              }}
            />
          </div>

          {/* Buttons Section */}
          <div className="flex gap-3 w-full items-center justify-center">
            <button
              onClick={downloadQRCode}
              className="bg-[#EE2B2A] text-white text-sm py-2 px-4 rounded-[10px] w-28"
            >
              <p className="text-nowrap text-[12px]">Download QR</p>
            </button>

            {/* Dropdown Section */}
            <div className="relative">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="bg-[#2E2F31] text-white px-4 py-2 rounded-[10px] flex items-center w-32 justify-between"
              >
                <span className="text-[#A6A6A6] text-sm text-nowrap">
                  {selectedOption}
                </span>
                <img
                  className={`ml-2 transition-transform ${
                    isOpen ? 'rotate-180' : 'rotate-0'
                  }`}
                  src={iconsPath?.vector6Image}
                  alt="Dropdown Icon"
                />
              </button>
              {isOpen && (
                <div className="absolute mt-1 w-full bg-[#2E2F31] z-[9999999] shadow-lg rounded-lg">
                  {options.map((option, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 hover:bg-gray-800 cursor-pointer"
                      onClick={() => {
                        setSelectedOption(option);
                        setIsOpen(false);
                      }}
                    >
                      <p className="text-[#A6A6A6] text-sm">{option}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Image Upload Section */}
          <div className="w-64 flex justify-center items-center">
            <label
              htmlFor="fileUpload"
              className="rounded-md py-2 px-3 flex items-center justify-center gap-2 cursor-pointer font-inter bg-[#2E2F31] shadow-sm text-[#989898] text-sm w-full h-[36px]"
            >
              <img
                src={iconsPath?.group8Image}
                alt="Upload Icon"
                className="h-4 w-4"
              />
              <span>
                {imagePath
                  ? imagePath.length > 15
                    ? imagePath.substring(0, 12) + '...'
                    : imagePath
                  : 'Add a Logo'}
              </span>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
                id="fileUpload"
              />
            </label>
          </div>

          {/* Public URL Inputs with Copy Icons */}
          <div className="w-full flex flex-col gap-3">
            {urls.map((url, index) => (
              <div key={index} className="w-72 relative">
                <label className="text-[#A6A6A6] text-sm block mb-1">
                  {labels[index]}
                </label>
                <div className="relative">
                  <input
                    value={url.substring(0, 25) + '...'}
                    type="text"
                    className="bg-[#2E2F31] text-[#989898] text-sm w-full p-2 pr-8 rounded-[5px] outline-none"
                    readOnly
                  />
                  <button
                    onClick={() => handleCopy(index, url)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[#A6A6A6] hover:text-white"
                  >
                    <img
                      className="h-4 w-4"
                      src={iconsPath?.group388Image}
                      alt=""
                    />
                  </button>

                  {/* Tooltip */}
                  {showTooltip.show && showTooltip.index === index && (
                    <div className="absolute right-0 -top-8 bg-[#2E2F31] text-white text-xs px-2 py-1 rounded">
                      Copied!
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Social Icons */}
          <div className="flex gap-3">
            {socialIcon.map((item) => (
              <div key={item.id}>
                <img
                  src={item.icon}
                  alt=""
                  className="h-4 w-4 cursor-pointer"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </SimpleModal>
  );
};

export default PublishData;

// import React, { useState, useRef, useEffect } from 'react';
// import { QRCode } from 'react-qrcode-logo';
// import { iconsPath, imagePath as imagePath1 } from '../../utils/imagePath';
// import { encrypt } from '../../utils/cryptoUtils';
// import ReactDOM from 'react-dom/client';
// import { SimpleModal } from '../ui/NormalModal';
// import store from '../../redux/store';
// import { setLoading } from '../../redux/Slice/globalStates';

// const PublishData = ({
//   projectId,
//   setPublished,
//   published,
//   onAfterPublish,
//   category,
// }) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const [selectedOption, setSelectedOption] = useState('180p');
//   const [imagePath, setImagePath] = useState('');
//   const [logoUrl, setLogoUrl] = useState(null); // Store uploaded logo URL
//   const [showTooltip, setShowTooltip] = useState({ index: null, show: false }); // For tooltip state
//   const [downloadSize, setDownloadSize] = useState(100);

//   const qrRef = useRef(null); // Reference for the QR Code

//   const options = ['180p', '360p', '480p'];
//   const labels = ['Public URL', 'Embed Link', 'Spatial Grid ID'];

//   const modalref = useRef(null);

//   const handleClickOutside = (event) => {
//     if (modalref.current && !modalref.current.contains(event.target)) {
//       setPublished(false);
//     }
//   };

//   useEffect(() => {
//     document.addEventListener('mousedown', handleClickOutside);
//     return () => {
//       document.removeEventListener('mousedown', handleClickOutside);
//     };
//   }, []);

//   // Generate URLs based on projectId
//   const urls = labels.map(
//     () =>
//       `${process.env.REACT_APP_IMAGE_BASE_URL}/api/v1/experience/detail/${projectId}`
//   );

//   // Handle file upload and set the logo
//   const handleImageChange = (e) => {
//     const file = e.target.files[0];
//     if (file) {
//       const fileReader = new FileReader();
//       fileReader.onload = (event) => {
//         setLogoUrl(event.target.result); // Convert image to Base64 URL
//       };
//       fileReader.readAsDataURL(file);
//       setImagePath(file.name);
//     }
//   };

//   const socialIcon = [
//     {
//       id: 1,
//       icon: imagePath1?.x,
//     },
//     {
//       id: 2,
//       icon: imagePath1?.instagram,
//     },
//     {
//       id: 3,
//       icon: imagePath1?.linkdin,
//     },
//     {
//       id: 4,
//       icon: imagePath1?.youtube,
//     },
//     {
//       id: 5,
//       icon: imagePath1?.discord,
//     },
//   ];

//   // Update download size when option changes
//   useEffect(() => {
//     switch (selectedOption) {
//       case '180p':
//         setDownloadSize(100);
//         break;
//       case '360p':
//         setDownloadSize(200);
//         break;
//       case '480p':
//         setDownloadSize(300);
//         break;
//       default:
//         setDownloadSize(100);
//     }
//   }, [selectedOption]);

//   // Handle QR Code Download
//   const downloadQRCode = () => {
//     // Create a temporary container
//     const tempContainer = document.createElement('div');
//     document.body.appendChild(tempContainer);

//     // Create a temporary QR code with the selected size
//     const tempQR = React.createElement(QRCode, {
//       value: urls[0],
//       size: downloadSize,
//       logoImage: logoUrl,
//       logoWidth: downloadSize * 0.2,
//       logoHeight: downloadSize * 0.2,
//       logoOpacity: 1,
//       removeQrCodeBehindLogo: false,
//       style: {
//         backgroundColor: '#F5F5F5',
//         padding: '0',
//         borderRadius: '20px',
//       },
//     });

//     // Render the QR code
//     const root = ReactDOM.createRoot(tempContainer);
//     root.render(tempQR);

//     // Wait for the QR code to render
//     setTimeout(() => {
//       const qrCanvas = tempContainer.querySelector('canvas');
//       if (qrCanvas) {
//         const qrImage = qrCanvas.toDataURL('image/png');
//         const link = document.createElement('a');
//         link.href = qrImage;
//         link.download = `qrcode-${selectedOption}.png`;
//         link.click();
//       }
//       // Clean up
//       root.unmount();
//       tempContainer.remove();
//     }, 100);
//   };

//   // Handle copy to clipboard and show tooltip
//   const handleCopy = (index, text) => {
//     navigator.clipboard.writeText(text).then(() => {
//       setShowTooltip({ index, show: true });
//       setTimeout(() => {
//         setShowTooltip({ index: null, show: false });
//       }, 2000); // Hide tooltip after 2 seconds
//     });
//   };

//   return (
//     <SimpleModal
//       isOpen={published}
//       onClose={() => {
//         setPublished('');
//         // if (onAfterPublish) onAfterPublish();
//         // window.location.reload(); // Removed reload
//       }}
//       className={
//         'bg-[#131418]  shadow-lg rounded-[10px] px-6 py-6 !max-w-[340px] relative'
//       }
//     >
//       <div
//         onClick={() => {
//           setPublished(false);
//           // if (onAfterPublish) onAfterPublish();
//           // window.location.reload(); // Removed reload
//         }}
//         className="absolute top-3 right-4  "
//       >
//         <button className="bg-[#31333A] px-2 py-2 rounded-full">
//           <img className="h-3 w-3" src={iconsPath?.group95Image} alt="" />
//         </button>
//       </div>
//       <div className="flex flex-col items-center gap-4">
//         <div className="flex flex-col">
//           <h1 className="text-[#F5F5F5] font-bold text-[16px] text-center">
//             Your Changes are Published!
//           </h1>
//           <p className="text-[#A6A6A6] text-[12px] text-center">
//             Scan this QR code to view your experience
//           </p>
//         </div>
//         {/* QR Code Generation */}
//         <div
//           ref={qrRef}
//           className="border-8 border-[#CACACA] h-[131px] w-[131px]  rounded-[20px]  relative  "
//         >
//           <QRCode
//             value={urls[0]}
//             size={300}
//             logoImage={logoUrl}
//             logoWidth={20}
//             logoHeight={20}
//             logoOpacity={1}
//             removeQrCodeBehindLogo={false}
//             style={{
//               position: 'relative',
//               top: '58px',
//               backgroundColor: '#F5F5F5',
//               boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.5)',
//               borderRadius: '20px',
//               right: '-1px',
//               maxWidth: '120px',
//               maxHeight: '120px',
//               padding: '10px',
//             }}
//           />
//         </div>

//         {/* Buttons Section */}
//         <div className="flex gap-3 w-full items-center justify-center">
//           <button
//             onClick={downloadQRCode}
//             className="bg-[#EE2B2A] text-white text-sm py-2 px-4 rounded-[10px] w-28"
//           >
//             <p className="text-nowrap text-[12px]"> Download QR</p>
//           </button>

//           {/* Dropdown Section */}
//           <div className="relative">
//             <button
//               onClick={() => setIsOpen(!isOpen)}
//               className="bg-[#2E2F31] text-white px-4 py-2 rounded-[10px]  flex items-center w-32 justify-between"
//             >
//               <span className="text-[#A6A6A6] text-sm text-nowrap">
//                 {selectedOption}
//               </span>
//               <img
//                 className={`ml-2 transition-transform ${
//                   isOpen ? 'rotate-180' : 'rotate-0'
//                 }`}
//                 src={iconsPath?.vector6Image}
//                 alt="Dropdown Icon"
//               />
//             </button>
//             {isOpen && (
//               <div className="absolute mt-1 w-full bg-[#2E2F31] z-[9999999] shadow-lg rounded-lg">
//                 {options.map((option, index) => (
//                   <div
//                     key={index}
//                     className="px-4 py-2 hover:bg-gray-800 cursor-pointer"
//                     onClick={() => {
//                       setSelectedOption(option);
//                       setIsOpen(false);
//                     }}
//                   >
//                     <p className="text-[#A6A6A6] text-sm">{option}</p>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>
//         </div>

//         {/* Image Upload Section */}
//         <div className="w-64 flex justify-center items-center">
//           <label
//             htmlFor="fileUpload"
//             className="rounded-md py-2 px-3 flex  items-center justify-center gap-2 cursor-pointer font-inter bg-[#2E2F31] shadow-sm text-[#989898] text-sm w-full h-[36px]"
//           >
//             <img
//               src={iconsPath?.group8Image}
//               alt="Upload Icon"
//               className="h-4 w-4 "
//             />
//             <span>
//               {imagePath
//                 ? imagePath.length > 15
//                   ? imagePath.substring(0, 12) + '...'
//                   : imagePath
//                 : 'Add a Logo'}
//             </span>
//             <input
//               type="file"
//               accept="image/*"
//               className="hidden"
//               onChange={handleImageChange}
//               id="fileUpload"
//             />
//           </label>
//         </div>

//         {/* Public URL Inputs with Copy Icons */}
//         <div className="w-full flex flex-col gap-3">
//           {urls.map((url, index) => (
//             <div key={index} className="w-72 relative">
//               <label className="text-[#A6A6A6] text-sm block mb-1">
//                 {labels[index]}
//               </label>
//               <div className="relative">
//                 <input
//                   value={url.substring(0, 25) + '...'}
//                   type="text"
//                   className="bg-[#2E2F31] text-[#989898] text-sm w-full p-2 pr-8 rounded-[5px] outline-none"
//                   readOnly
//                 />
//                 <button
//                   onClick={() => handleCopy(index, url)}
//                   className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[#A6A6A6] hover:text-white"
//                 >
//                   <img
//                     className="h-4 w-4"
//                     src={iconsPath?.group388Image}
//                     alt=""
//                   />
//                 </button>

//                 {/* Tooltip */}
//                 {showTooltip.show && showTooltip.index === index && (
//                   <div className="absolute right-0 -top-8 bg-[#2E2F31] text-white text-xs px-2 py-1 rounded">
//                     Copied!
//                   </div>
//                 )}
//               </div>
//             </div>
//           ))}
//         </div>
//         <div className="flex gap-3">
//           {socialIcon.map((item) => {
//             return (
//               <div key={item.id}>
//                 <img
//                   src={item.icon}
//                   alt=""
//                   className="h-4 w-4 cursor-pointer"
//                 />
//               </div>
//             );
//           })}
//         </div>
//       </div>
//     </SimpleModal>
//     // <div className="fixed inset-0 flex justify-center z-[9999] items-center bg-black backdrop-blur-sm bg-opacity-50">
//     //   <div
//     //     ref={modalref}
//     //     className="bg-[#131418] w-fit z-[999999] relative shadow-lg rounded-[10px] px-6 py-6"
//     //   >

//     //   </div>
//     // </div>
//   );
// };

// export default PublishData;
